import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {handleErrorResponse} from "../utils/util"
import { USER_ROLE } from "../constants/index"
import { HistoryService } from "../api/services.gen";
import {
    HistoryListData,
} from "../api/types.gen";
import i18n from 'i18next';
import { resetAppState } from "../store/common";

export const getHistoryList = createAsyncThunk(
    "getHistoryList",
    async (data: HistoryListData, thunkAPI: any) => {
        try {
            const res: any = await HistoryService.historyList(data);
            if (res.results.success) {
                return res;
            } else {
                if(res.message) return thunkAPI.rejectWithValue(res.message);
                else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
            }
        } catch (error: any) {
            return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
        }
    },
);

export interface TableHeader {
    title: string;
    isSort: boolean;
    field: string;
    currentSortState: number;
    customStyle: string;
  }
  
  const initTableHeaders = [
    {
        title: "操作日時",
        isSort: true,
        field: "created_at",
        currentSortState: 0,
        customStyle: "min-w-[180px] max-w-[180px]",
    },
    {
        title: "氏名",
        isSort: true,
        field: "fullname",
        currentSortState: 0,
        customStyle: "min-w-[160px]",
    },
    {
        title: "所属",
        isSort: true,
        field: "staff__department",
        currentSortState: 0,
        customStyle: "min-w-[130px] max-w-[220px]",
    },
    {
        title: "役職",
        isSort: true,
        field: "staff__position",
        currentSortState: 0,
        customStyle: "min-w-[130px] max-w-[160px]",
    },
    {
        title: "権限",
        isSort: true,
        field: "staff__type",
        currentSortState: 0,
        customStyle: "w-[120px]",
    },
    {
        title: "操作内容",
        isSort: true,
        field: "type",
        currentSortState: 0,
        customStyle: "w-[120px]",
    },
    {
        title: "操作画面",
        isSort: true,
        field: "screen_name",
        currentSortState: 0,
        customStyle: "w-[200px]",
    },
    {
        title: "操作詳細",
        isSort: false,
        field: "detail",
        currentSortState: 0,
        customStyle: "min-w-[130px]",
    },
];

// Initial state of Redux
interface CompanyHistoryState {
    loading: boolean;
    error: string | null;
    success: string | null;
    actionName: string | null;
    historyList: [];
    countItems: number;
    currentPage: number;
    pageSize: number;
    sortBy: string | null;
    sortOrder: string | null;
    searchQuery: string | null;
    totalResults: number;
    tableHeaders: TableHeader[];
}

const initialState: CompanyHistoryState = {
    loading: false,
    error: null,
    success: null,
    actionName: null,
    historyList: [],
    countItems: 0,
    currentPage: 1,
    pageSize: 50,
    sortBy: null,
    sortOrder: null,
    searchQuery: "",
    totalResults: 0,
    tableHeaders: initTableHeaders,
};

const sortingStates = ["default", "desc", "asc"];

export const companyHistorySlice = createSlice({
    name: "companyHistorySlice",
    initialState,
    reducers: {
        setSuccess: (state, action) => {
            state.success = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
        setSearchQuery: (state, action) => {
            state.searchQuery = action.payload;
        },
        handleSortByHeader(state, action) {
            const sortHeader = action.payload;
            state.tableHeaders = state.tableHeaders.map((header, idx) => (header.field === sortHeader.field)
            ? ({...header, currentSortState: (header.currentSortState + 1) % sortingStates.length})
            : ({...header, currentSortState: 0}));
            
            if((sortHeader.currentSortState + 1) % sortingStates.length !== 0) {
                state.sortBy = sortHeader.field;
                state.sortOrder = sortingStates[(sortHeader.currentSortState + 1) % sortingStates.length];
            } else {
                state.sortBy = null;
                state.sortOrder = null;
            }
        },
        setPageSize: (state, action) => {
            state.pageSize = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
        // reset state
        .addCase(resetAppState, () => initialState)

        // getHistoryList
        .addCase(getHistoryList.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getHistoryList.fulfilled, (state, action) => {
            state.loading = false;
            state.historyList = action.payload?.results?.data || [];
            state.totalResults = action.payload?.results?.total_records;
            state.countItems = action.payload?.count;
        })
        .addCase(getHistoryList.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        })
    }
});
export const {
    setSuccess,
    setError,
    setCurrentPage,
    setSearchQuery,
    handleSortByHeader,
    setPageSize,
} = companyHistorySlice.actions;
export default companyHistorySlice.reducer;